import type { ResultOf } from '@graphql-typed-document-node/core'
import type { GET_FILTERED_NOVELTIES, GET_FILTERED_COLLECTION_ENTRIES } from '@/queries'
import type {
  GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES,
  GET_DATABASE_LIST_CURATED_ENTRIES,
} from '@/queries/customLists.ts'
import { GET_NOVELTIES } from '@/queries'
import { CATALOG_OPS } from './catalogOps.generated.ts'
import type { SqliteExecutor, SqlValue, SqliteRow } from './sqliteExecutor.ts'
import {
  createFilterBuilder,
  renderSqlite,
  sqliteDialect,
  raw,
  buildFilteredNoveltyIdsSql,
  buildFilteredCustomListNoveltyIdsSql,
  buildFilteredUserReleaseEntryStatusIdsSql,
  buildFilteredCustomListReleaseEntryIdsSql,
  buildDatabaseListReleaseEntryIdsSql,
  buildUserCustomListStatsSql,
  extractTargetMonth,
  type ColumnMap,
  type FilteredNoveltyTables,
  type FilterContext,
  type SqlValue as QuerySqlValue,
} from 'atsume-query-core'
import {
  ENTITY_REPLICA_CONFIGS,
  type EntityReplicaConfig,
} from './entityReplicaConfig.generated.ts'

// The where/orderBy dialect comes from the shared atsume-query-core builder —
// the same one the Keystone backend renders to Postgres — so the local SQLite
// repository can't drift from the server. The per-entity ColumnMap is derived
// from the generated config; the parity test stays as a belt-and-suspenders.

type WhereInput = Record<string, unknown>
type OrderByInput = Record<string, 'asc' | 'desc'>

type NoveltyRow = NonNullable<ResultOf<typeof GET_NOVELTIES>['novelties']>[number]

function columnMapFor(config: EntityReplicaConfig): ColumnMap {
  return Object.fromEntries(
    config.columns.map((column) => [column.field, { sqlName: column.col, type: column.type }]),
  )
}

// The shared builder's value type allows booleans (Postgres binds them); SQLite
// drivers do not, so narrow them to 0/1 for the executor.
function toExecutorParams(params: readonly QuerySqlValue[]): SqlValue[] {
  return params.map((value) => (typeof value === 'boolean' ? (value ? 1 : 0) : value))
}

// The GET_NOVELTIES output projection, sourced from the generated op registry so it
// can never drift from the app's actual selection set (a hand-maintained copy here
// silently missed `thumbHash`). The replica blob holds the full delta payload; the
// repo projects it down to this field set on read. filteredNovelties returns the same
// novelty shape (collectionVolumeInfo is layered on separately), so it reuses this.
const NOVELTIES_OP = CATALOG_OPS.find((op) => op.document === GET_NOVELTIES)
if (!NOVELTIES_OP) {
  throw new Error('GET_NOVELTIES is not a registered catalog op — regenerate catalogOps')
}
export const NOVELTY_OUTPUT_FIELDS = NOVELTIES_OP.outputFields

type QueryArgs = {
  where?: WhereInput
  // Single-or-array mirrors GraphQL list-input coercion; buildOrderBy normalizes it.
  orderBy?: OrderByInput | OrderByInput[]
  take?: number | null
  skip?: number
}

export function parseRowData(row: SqliteRow): Record<string, unknown> {
  const data = row.data
  if (typeof data !== 'string') throw new Error('replica row missing data blob')
  return JSON.parse(data) as Record<string, unknown>
}

export function projectOutput(
  data: Record<string, unknown>,
  outputFields: readonly string[],
): Record<string, unknown> {
  const output: Record<string, unknown> = {}
  for (const field of outputFields) output[field] = data[field]
  return output
}

// The full ordered blobs for a where/orderBy/take/skip — the nested resolver needs
// the whole blob (its relation `{ id }` links live outside any single output
// projection). queryEntity projects these down for the flat reads.
export async function queryEntityRows(
  executor: SqliteExecutor,
  config: EntityReplicaConfig,
  args: QueryArgs,
): Promise<Record<string, unknown>[]> {
  const builder = createFilterBuilder('', columnMapFor(config))
  const where = renderSqlite(builder.buildWhere(args.where ?? {}), sqliteDialect)
  const orderBy = renderSqlite(builder.buildOrderBy(args.orderBy ?? []), sqliteDialect).sql
  const limit = args.take === undefined || args.take === null ? -1 : args.take
  const offset = args.skip ?? 0

  const rows = await executor.query(
    `SELECT data FROM "${config.table}" WHERE ${where.sql} ORDER BY ${orderBy} LIMIT ? OFFSET ?`,
    [...toExecutorParams(where.params), limit, offset],
  )
  return rows.map((row) => parseRowData(row))
}

export async function queryEntity(
  executor: SqliteExecutor,
  config: EntityReplicaConfig,
  outputFields: readonly string[],
  args: QueryArgs,
): Promise<Record<string, unknown>[]> {
  const rows = await queryEntityRows(executor, config, args)
  return rows.map((data) => projectOutput(data, outputFields))
}

const NOVELTY_CONFIG = ENTITY_REPLICA_CONFIGS.find((config) => config.entity === 'Novelty')
if (!NOVELTY_CONFIG) throw new Error('Missing generated replica config for Novelty')

export async function queryNovelties(
  executor: SqliteExecutor,
  args: QueryArgs,
): Promise<NoveltyRow[]> {
  const rows = await queryEntity(executor, NOVELTY_CONFIG, NOVELTY_OUTPUT_FIELDS, args)
  return rows as NoveltyRow[]
}

type FilteredNoveltyRow = NonNullable<
  ResultOf<typeof GET_FILTERED_NOVELTIES>['filteredNovelties']
>[number]

export function configForEntity(entity: string): EntityReplicaConfig {
  const config = ENTITY_REPLICA_CONFIGS.find((entry) => entry.entity === entity)
  if (!config) throw new Error(`Missing generated replica config for ${entity}`)
  return config
}

function tableFor(entity: string): string {
  return configForEntity(entity).table
}

// Fetch catalog replica rows by id and project each to the given output fields,
// keyed by id. Used by curated-list reads to join membership (from the user-sync
// store) to the catalog blobs — the client-side equivalent of the server's
// membership → releaseEntry/novelty join.
export async function projectEntitiesByIds(
  executor: SqliteExecutor,
  entity: string,
  outputFields: readonly string[],
  ids: readonly string[],
): Promise<Map<string, Record<string, unknown>>> {
  if (ids.length === 0) return new Map()
  const placeholders = ids.map(() => '?').join(', ')
  const rows = await executor.query(
    `SELECT data FROM "${tableFor(entity)}" WHERE "id" IN (${placeholders})`,
    [...ids],
  )
  return new Map(
    rows
      .map((row) => parseRowData(row))
      .map((data) => [data.id as string, projectOutput(data, outputFields)]),
  )
}

// The offline `filteredNovelties` join. The collection-aware predicate is the
// shared atsume-query-core builder — the SAME tree the Keystone backend renders
// to Postgres — so the offline result cannot drift from a live fetch. The user's
// collection lives in the mirror tables (userDataTables.ts) with the exact
// Postgres column names. Catalog tables are the snake_case replica tables; the
// novelty `releaseItems` comes from the blob (no dedicated column on the replica),
// and the single-user mirror needs no `user` predicate.
const SQLITE_TABLES: FilteredNoveltyTables = {
  novelty: NOVELTY_CONFIG.table,
  userReleaseEntryStatus: 'UserReleaseEntryStatus',
  userReleaseReadStatus: 'UserReleaseReadStatus',
  releaseItemCollection: tableFor('ReleaseItemCollection'),
  releaseItem: tableFor('ReleaseItem'),
  releaseItemCollectionLink: '_ReleaseItem_collections',
}

type CollectionVolumeInfo = NonNullable<FilteredNoveltyRow['collectionVolumeInfo']>

// Mirrors the server's `collectionVolumeInfo` virtual field: for a collection
// novelty, read its member items (the collection blob carries `releaseItems { id }`),
// then join to the ReleaseItem replica for each member's entry to derive the span
// flag. Non-collection novelties resolve to null, exactly like the resolver.
//
// Batched: rather than 2 queries per novelty (an N+1 fan-out across a page), it
// takes the distinct collection ids, fetches every collection blob in one `IN`
// query and every member ReleaseItem in a second, then maps synchronously.
async function batchReconstructCollectionVolumeInfo(
  executor: SqliteExecutor,
  collectionIds: readonly string[],
): Promise<Map<string, CollectionVolumeInfo>> {
  const result = new Map<string, CollectionVolumeInfo>()
  if (collectionIds.length === 0) return result

  const collectionPlaceholders = collectionIds.map(() => '?').join(', ')
  const collectionRows = await executor.query(
    `SELECT data FROM "${SQLITE_TABLES.releaseItemCollection}" WHERE "id" IN (${collectionPlaceholders})`,
    [...collectionIds],
  )
  // volumeCount mirrors the resolver's raw `releaseItems.length` (before id
  // filtering); itemIds are the valid links used to derive the span flag.
  const volumeCountByCollection = new Map<string, number>()
  const itemIdsByCollection = new Map<string, string[]>()
  const allItemIds = new Set<string>()
  for (const row of collectionRows) {
    const collection = parseRowData(row)
    if (typeof collection.id !== 'string') continue
    const items = Array.isArray(collection.releaseItems) ? collection.releaseItems : []
    const itemIds = items
      .map((item) => (item as { id?: unknown }).id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
    volumeCountByCollection.set(collection.id, items.length)
    itemIdsByCollection.set(collection.id, itemIds)
    for (const itemId of itemIds) allItemIds.add(itemId)
  }

  const entryIdByItemId = new Map<string, string>()
  if (allItemIds.size > 0) {
    const itemIds = [...allItemIds]
    const itemPlaceholders = itemIds.map(() => '?').join(', ')
    const itemRows = await executor.query(
      `SELECT data FROM "${SQLITE_TABLES.releaseItem}" WHERE "id" IN (${itemPlaceholders})`,
      itemIds,
    )
    for (const row of itemRows) {
      const data = parseRowData(row)
      if (typeof data.id === 'string' && typeof data.entryId === 'string' && data.entryId.length > 0)
        entryIdByItemId.set(data.id, data.entryId)
    }
  }

  for (const collectionId of collectionIds) {
    // Missing collection blob resolves the same as the per-row fallback did.
    if (!itemIdsByCollection.has(collectionId)) {
      result.set(collectionId, {
        __typename: 'NoveltyCollectionVolumeInfo',
        volumeCount: 0,
        spansEntries: false,
      })
      continue
    }
    const entryIds = new Set<string>()
    for (const itemId of itemIdsByCollection.get(collectionId) ?? []) {
      const entryId = entryIdByItemId.get(itemId)
      if (entryId) entryIds.add(entryId)
    }
    result.set(collectionId, {
      __typename: 'NoveltyCollectionVolumeInfo',
      volumeCount: volumeCountByCollection.get(collectionId) ?? 0,
      spansEntries: entryIds.size > 1,
    })
  }
  return result
}

async function withCollectionVolumeInfo(
  executor: SqliteExecutor,
  novelties: readonly Record<string, unknown>[],
): Promise<FilteredNoveltyRow[]> {
  const collectionIds = [
    ...new Set(
      novelties
        .filter((n) => n.relationType === 'collection' && typeof n.relationId === 'string')
        .map((n) => n.relationId as string),
    ),
  ]
  const infoByCollectionId = await batchReconstructCollectionVolumeInfo(executor, collectionIds)
  return novelties.map((novelty) => ({
    ...novelty,
    collectionVolumeInfo:
      novelty.relationType === 'collection' && typeof novelty.relationId === 'string'
        ? (infoByCollectionId.get(novelty.relationId) ?? null)
        : null,
  })) as FilteredNoveltyRow[]
}

type FilteredQueryArgs = QueryArgs & { filterMode?: string | null }

export async function queryFilteredNovelties(
  executor: SqliteExecutor,
  args: FilteredQueryArgs,
): Promise<FilteredNoveltyRow[]> {
  // No filterMode: the server returns a plain Novelty.findMany (no collection
  // join). Same here, then layer collectionVolumeInfo on top.
  if (!args.filterMode) {
    const base = await queryEntity(executor, NOVELTY_CONFIG, NOVELTY_OUTPUT_FIELDS, args)
    return withCollectionVolumeInfo(executor, base)
  }

  const builder = createFilterBuilder('n', columnMapFor(NOVELTY_CONFIG))
  const node = buildFilteredNoveltyIdsSql({
    where: builder.buildWhere(args.where ?? {}),
    orderBy: builder.buildOrderBy(args.orderBy ?? []),
    filterMode: args.filterMode,
    take: args.take,
    skip: args.skip ?? 0,
    targetMonth: extractTargetMonth(args.where ?? {}),
    tables: SQLITE_TABLES,
    releaseItems: raw(`json_extract(n."data", '$.releaseItems')`),
    noveltyNumberText: raw(`json_extract(n."data", '$.number')`),
    releaseItemNumberText: raw(`json_extract(ri."data", '$.number')`),
    entryUserMatch: raw('TRUE'),
  })
  return idsToFilteredNovelties(executor, node)
}

type FilteredCustomListQueryArgs = FilteredQueryArgs & { listId: string }

export async function queryFilteredCustomListNovelties(
  executor: SqliteExecutor,
  args: FilteredCustomListQueryArgs,
): Promise<FilteredNoveltyRow[]> {
  const builder = createFilterBuilder('n', columnMapFor(NOVELTY_CONFIG))
  const node = buildFilteredCustomListNoveltyIdsSql({
    listId: args.listId,
    where: builder.buildWhere(args.where ?? {}),
    orderBy: builder.buildOrderBy(args.orderBy ?? []),
    filterMode: args.filterMode ?? null,
    take: args.take,
    skip: args.skip ?? 0,
    targetMonth: extractTargetMonth(args.where ?? {}),
    tables: { ...SQLITE_TABLES, userCustomListNoveltyItem: 'UserCustomListNoveltyItem' },
    releaseItems: raw(`json_extract(n."data", '$.releaseItems')`),
    noveltyNumberText: raw(`json_extract(n."data", '$.number')`),
    releaseItemNumberText: raw(`json_extract(ri."data", '$.number')`),
    entryUserMatch: raw('TRUE'),
    listUserMatch: raw('TRUE'),
  })
  return idsToFilteredNovelties(executor, node)
}

// Run an id-selecting filtered-novelty query node, then replay the ordered blobs
// to the GET_NOVELTIES projection and layer collectionVolumeInfo on top.
async function idsToFilteredNovelties(
  executor: SqliteExecutor,
  node: ReturnType<typeof buildFilteredNoveltyIdsSql>,
): Promise<FilteredNoveltyRow[]> {
  const rendered = renderSqlite(node, sqliteDialect)
  const idRows = await executor.query(rendered.sql, toExecutorParams(rendered.params))
  const ids = idRows.map((row) => row.id as string)
  if (ids.length === 0) return []

  const placeholders = ids.map(() => '?').join(', ')
  const blobRows = await executor.query(
    `SELECT data FROM "${NOVELTY_CONFIG.table}" WHERE "id" IN (${placeholders})`,
    ids,
  )
  const byId = new Map(blobRows.map((row) => parseRowData(row)).map((data) => [data.id, data]))
  const ordered = ids
    .map((id) => byId.get(id))
    .filter((data): data is Record<string, unknown> => data !== undefined)
    .map((data) => projectOutput(data, NOVELTY_OUTPUT_FIELDS))
  return withCollectionVolumeInfo(executor, ordered)
}

// The offline `filteredUserReleaseEntryStatuses` (collection lists). The
// completeness/gaps predicate is the shared atsume-query-core builder (same tree
// the server renders to Postgres). The filter/sort run on the user mirror
// (userDataTables), then each passing row's `entry { ... }` is joined from the
// catalog ReleaseEntry replica by id — matching the query's selection set.
type CollectionEntryRow = NonNullable<
  ResultOf<typeof GET_FILTERED_COLLECTION_ENTRIES>['filteredUserReleaseEntryStatuses']
>[number]

type EntryOutput = NonNullable<CollectionEntryRow['entry']>

const URS_COLUMN_MAP: ColumnMap = {
  id: { sqlName: 'id', type: 'text' },
  compoundKey: { sqlName: 'compoundKey', type: 'text' },
  createdAt: { sqlName: 'createdAt', type: 'text' },
  lastCollectionChanged: { sqlName: 'lastCollectionChanged', type: 'text' },
  lastReadChanged: { sqlName: 'lastReadChanged', type: 'text' },
  endDate: { sqlName: 'endDate', type: 'text' },
  startDate: { sqlName: 'startDate', type: 'text' },
  internalName: { sqlName: 'internalName', type: 'text' },
  type: { sqlName: 'type', type: 'text' },
  publisherName: { sqlName: 'publisherName', type: 'text' },
  popularityScore: { sqlName: 'popularityScore', type: 'numeric' },
  recommendationScore: { sqlName: 'recommendationScore', type: 'numeric' },
  tagSearchField: { sqlName: 'tagSearchField', type: 'text' },
  demographySearchField: { sqlName: 'demographySearchField', type: 'text' },
  totalVolumes: { sqlName: 'totalVolumes', type: 'int' },
  totalNotReleasedVolumes: { sqlName: 'totalNotReleasedVolumes', type: 'int' },
  totalReleasedVolumes: { sqlName: 'totalReleasedVolumes', type: 'int' },
  titleSearch: { sqlName: 'titleSearch', type: 'text' },
  currentReadStatus: { sqlName: 'currentReadStatus', type: 'text' },
  collectionStatus: { sqlName: 'collectionStatus', type: 'text' },
}

const ENTRY_OUTPUT_FIELDS: readonly string[] = [
  'id',
  'type',
  'cover',
  'thumbHash',
  'status',
  'sortedVolumeNumbers',
  'notReleasedVolumeNumbers',
  'totalReleasedVolumes',
]

export async function queryFilteredCollectionEntries(
  executor: SqliteExecutor,
  args: FilteredQueryArgs,
): Promise<CollectionEntryRow[]> {
  const builder = createFilterBuilder('urs', URS_COLUMN_MAP)
  const context: FilterContext =
    'currentReadStatus' in (args.where ?? {}) ? 'read' : 'collection'
  const node = buildFilteredUserReleaseEntryStatusIdsSql({
    where: builder.buildWhere(args.where ?? {}),
    orderBy: builder.buildOrderBy(args.orderBy ?? []),
    filterMode: args.filterMode ?? null,
    context,
    take: args.take,
    skip: args.skip ?? 0,
    tables: {
      userReleaseEntryStatus: 'UserReleaseEntryStatus',
      userReleaseReadStatus: 'UserReleaseReadStatus',
    },
    userMatch: raw('TRUE'),
  })
  const rendered = renderSqlite(node, sqliteDialect)
  const idRows = await executor.query(rendered.sql, toExecutorParams(rendered.params))
  const ids = idRows.map((row) => row.id as string)
  if (ids.length === 0) return []

  const idPlaceholders = ids.map(() => '?').join(', ')
  const ursRows = await executor.query(
    `SELECT "id", "entry", "currentReadStatus", "internalName", "publisherName", "startDate", "endDate"
     FROM "UserReleaseEntryStatus" WHERE "id" IN (${idPlaceholders})`,
    ids,
  )
  const ursById = new Map(ursRows.map((row) => [row.id as string, row]))

  const entryIds = [...new Set(ursRows.map((row) => row.entry as string))]
  const entryPlaceholders = entryIds.map(() => '?').join(', ')
  const entryBlobs =
    entryIds.length > 0
      ? await executor.query(
          `SELECT data FROM "${tableFor('ReleaseEntry')}" WHERE "id" IN (${entryPlaceholders})`,
          entryIds,
        )
      : []
  const entryById = new Map(
    entryBlobs
      .map((row) => parseRowData(row))
      .map((data) => [data.id as string, projectOutput(data, ENTRY_OUTPUT_FIELDS) as EntryOutput]),
  )

  return ids.flatMap((id) => {
    const urs = ursById.get(id)
    if (!urs) return []
    return [
      {
        __typename: 'UserReleaseEntryStatus',
        id: urs.id as string,
        entry: entryById.get(urs.entry as string) ?? null,
        currentReadStatus: urs.currentReadStatus,
        internalName: urs.internalName,
        publisherName: urs.publisherName,
        startDate: urs.startDate,
        endDate: urs.endDate,
      } as CollectionEntryRow,
    ]
  })
}

// The offline `filteredReleaseEntries` (curated release-entry lists). The
// membership ⋈ ReleaseEntry ⋈ ownership predicate is the shared query-core
// builder (same tree the server renders to Postgres); the ordered ids are then
// projected from the catalog ReleaseEntry replica blobs.
type CustomListReleaseEntryRow = NonNullable<
  ResultOf<typeof GET_FILTERED_CUSTOM_LIST_RELEASE_ENTRIES>['filteredReleaseEntries']
>[number]

const RELEASE_ENTRY_LIST_FIELDS: readonly string[] = [
  'cover',
  'thumbHash',
  'id',
  'status',
  'internalName',
  'type',
  'publisherName',
  'startDate',
  'sortedVolumeNumbers',
  'notReleasedVolumeNumbers',
]

export async function queryFilteredCustomListReleaseEntries(
  executor: SqliteExecutor,
  args: FilteredQueryArgs & { listId: string },
): Promise<CustomListReleaseEntryRow[]> {
  const releaseEntryConfig = configForEntity('ReleaseEntry')
  const builder = createFilterBuilder('re', columnMapFor(releaseEntryConfig))
  const node = buildFilteredCustomListReleaseEntryIdsSql({
    listId: args.listId,
    where: builder.buildWhere(args.where ?? {}),
    orderBy: builder.buildOrderBy(args.orderBy ?? []),
    filterMode: args.filterMode ?? null,
    take: args.take,
    skip: args.skip ?? 0,
    tables: {
      userCustomListReleaseEntryItem: 'UserCustomListReleaseEntryItem',
      releaseEntry: releaseEntryConfig.table,
      userReleaseEntryStatus: 'UserReleaseEntryStatus',
      userReleaseReadStatus: 'UserReleaseReadStatus',
    },
    entryUserMatch: raw('TRUE'),
    listUserMatch: raw('TRUE'),
  })
  const rendered = renderSqlite(node, sqliteDialect)
  const idRows = await executor.query(rendered.sql, toExecutorParams(rendered.params))
  const ids = idRows.map((row) => row.id as string)
  if (ids.length === 0) return []

  const byId = await projectEntitiesByIds(executor, 'ReleaseEntry', RELEASE_ENTRY_LIST_FIELDS, ids)
  return ids.flatMap((id) => {
    const entry = byId.get(id)
    return entry ? [entry as CustomListReleaseEntryRow] : []
  })
}

// The offline `databaseListReleaseEntries` (curated database lists). Membership ⋈
// ReleaseEntry, filtered by the list's databaseList FK + a ReleaseEntry sub-where,
// ordered by the curated orderNumber. Served via the shared query-core builder.
type DatabaseListReleaseEntryRow = NonNullable<
  ResultOf<typeof GET_DATABASE_LIST_CURATED_ENTRIES>['databaseListReleaseEntries']
>[number]

export async function queryDatabaseListReleaseEntries(
  executor: SqliteExecutor,
  args: {
    databaseListId: string
    where?: WhereInput
    direction: 'asc' | 'desc'
    take?: number | null
    skip?: number
  },
): Promise<DatabaseListReleaseEntryRow[]> {
  const membershipTable = configForEntity('DatabaseListReleaseEntry').table
  const builder = createFilterBuilder('re', columnMapFor(configForEntity('ReleaseEntry')))
  const node = buildDatabaseListReleaseEntryIdsSql({
    databaseListId: args.databaseListId,
    where: builder.buildWhere(args.where ?? {}),
    direction: args.direction,
    take: args.take,
    skip: args.skip ?? 0,
    tables: {
      databaseListReleaseEntry: membershipTable,
      releaseEntry: tableFor('ReleaseEntry'),
    },
  })
  const rendered = renderSqlite(node, sqliteDialect)
  const idRows = await executor.query(rendered.sql, toExecutorParams(rendered.params))
  const ids = idRows.map((row) => row.id as string)
  if (ids.length === 0) return []

  const idPlaceholders = ids.map(() => '?').join(', ')
  const membershipRows = await executor.query(
    `SELECT "id", "orderNumber", "releaseEntry" FROM "${membershipTable}" WHERE "id" IN (${idPlaceholders})`,
    ids,
  )
  const membershipById = new Map(membershipRows.map((row) => [row.id as string, row]))

  const entryIds = [...new Set(membershipRows.map((row) => row.releaseEntry as string))]
  const entryById = await projectEntitiesByIds(executor, 'ReleaseEntry', RELEASE_ENTRY_LIST_FIELDS, entryIds)

  return ids.flatMap((id) => {
    const membership = membershipById.get(id)
    if (!membership) return []
    const releaseEntry = entryById.get(membership.releaseEntry as string)
    return releaseEntry
      ? [
          {
            __typename: 'DatabaseListReleaseEntry',
            id: membership.id as string,
            orderNumber: membership.orderNumber,
            releaseEntry,
          } as DatabaseListReleaseEntryRow,
        ]
      : []
  })
}

export type CustomListStatsRow = {
  itemCount: number
  seriesCount: number
  volumeCount: number
  totalValue: number
}

// Offline userCustomListStats: the same shared node the server renders to Postgres,
// rendered here to SQLite against the JSON-blob replica. Membership is materialized
// just-in-time by the caller (refreshList*Membership), so this reads the fresh set.
export async function queryUserCustomListStats(
  executor: SqliteExecutor,
  args: { listId: string; targetType: 'novelty' | 'releaseEntry' },
): Promise<CustomListStatsRow> {
  const node =
    args.targetType === 'novelty'
      ? buildUserCustomListStatsSql({
          targetType: 'novelty',
          listId: args.listId,
          tables: { membership: 'UserCustomListNoveltyItem', novelty: NOVELTY_CONFIG.table },
          listUserMatch: raw('TRUE'),
          noveltyPrice: raw(`json_extract(n."data", '$.price')`),
        })
      : buildUserCustomListStatsSql({
          targetType: 'releaseEntry',
          listId: args.listId,
          tables: {
            membership: 'UserCustomListReleaseEntryItem',
            releaseEntry: tableFor('ReleaseEntry'),
            releaseItem: tableFor('ReleaseItem'),
            releaseItemCollection: tableFor('ReleaseItemCollection'),
          },
          listUserMatch: raw('TRUE'),
          sortedVolumeNumbers: raw(`json_extract(re."data", '$.sortedVolumeNumbers')`),
          releaseItemPrice: raw(`json_extract(ri."data", '$.price')`),
          releaseItemEntryId: raw(`json_extract(ri."data", '$.entryId')`),
          releaseItemCollectionPrice: raw(`json_extract(rc."data", '$.price')`),
          releaseItemCollectionEntryId: raw(`json_extract(rc."data", '$.entryId')`),
        })
  const rendered = renderSqlite(node, sqliteDialect)
  const rows = await executor.query(rendered.sql, toExecutorParams(rendered.params))
  const row = rows[0] ?? {}
  return {
    itemCount: Number(row.itemCount ?? 0),
    seriesCount: Number(row.seriesCount ?? 0),
    volumeCount: Number(row.volumeCount ?? 0),
    totalValue: Number(row.totalValue ?? 0),
  }
}
